/**
 * 联系电话校验工具
 *
 * 背景：旧代码写死 /^1[3456789]\d{9}$/，只认中国大陆 11 位手机号，
 * 境外号码（如 +971 561970866）会被误判为「手机号码有误」。
 *
 * 现支持（兼容式，任一命中即通过）：
 *  - 中国大陆手机号；
 *  - 阿联酋 / 沙特 / 巴西：带国际区号、带本地 0 前缀或不带前缀均可；
 *  - 其它国家：未锁定到受支持国家时，放行通用国际号码（6~15 位），避免误拦。
 * 校验前统一去除空格、横线、括号、点等分隔符，并兼容 + / 00 前缀。
 */

// 去除常见分隔符：空格、横线（含全角）、括号、点
export function normalizePhone(tel) {
  if (tel === null || tel === undefined) return "";
  return String(tel).replace(/[\s\-－()（）.．]/g, "");
}

// 去掉国际前缀 + / 00，得到纯数字串（可能含国家码）
function toDigits(tel) {
  return normalizePhone(tel).replace(/^\+/, "").replace(/^00/, "");
}

/**
 * 各国/地区号码规则
 * re 针对「已去掉 + / 00 的数字串」，同时兼容带国家码 / 带本地 0 前缀两种写法
 */
export const COUNTRY_PHONE_RULES = [
  {
    code: "86",
    names: ["中国", "中国大陆", "china", "cn"],
    // 手机：1 开头 11 位（可带 86 前缀）
    re: /^(?:86)?1[3-9]\d{9}$/,
  },
  {
    code: "971",
    names: ["阿联酋", "阿拉伯联合酋长国", "uae", "united arab emirates", "ae"],
    // 手机 5X XXXXXXX；固话 2/3/4/6/7/9 + 7 位；可带 971 或本地 0 前缀
    re: /^(?:971|0)?(?:5\d{8}|[234679]\d{7})$/,
  },
  {
    code: "966",
    names: ["沙特", "沙特阿拉伯", "saudi arabia", "saudi", "sa", "ksa"],
    // 手机 5X XXXXXXX；固话 11~17 + 7 位；可带 966 或本地 0 前缀
    re: /^(?:966|0)?(?:5\d{8}|1[1-7]\d{7})$/,
  },
  {
    code: "55",
    names: ["巴西", "brazil", "br"],
    // 手机 AA 9XXXXXXXX（11 位）；固话 AA + 2~5 + 7 位；可带 55 或 0 前缀
    re: /^(?:55|0)?(?:\d{2}9\d{8}|\d{2}[2-5]\d{7})$/,
  },
];

// 按国家名 / 国际区号查找规则（中文名、英文名、ISO 码、区号均可）
export function findCountryRule(country) {
  if (!country) return null;
  const key = String(country).trim().toLowerCase();
  if (!key) return null;
  return (
    COUNTRY_PHONE_RULES.find(
      (r) => r.code === key || r.names.some((n) => n.toLowerCase() === key)
    ) || null
  );
}

// 是否为中国大陆手机号（1 开头，第二位 3-9，共 11 位）
export function isChinaMobile(tel) {
  return /^(?:86)?1[3-9]\d{9}$/.test(toDigits(tel));
}

/**
 * 校验联系电话
 *
 * 兼容范围（任一命中即通过）：
 *  - 中国大陆手机号
 *  - 阿联酋 / 沙特 / 巴西（带国际区号、带本地 0 前缀或不带前缀均可）
 * 其它国家的号码：若未锁定到受支持国家，则放行通用国际号码（6~15 位），避免误拦。
 *
 * @param {string} tel     号码，可含 +、空格、横线、括号等
 * @param {string} [country] 所选国家/地区（中文名 / 英文名 / ISO 码 / 区号），可选
 * @returns {boolean}
 */
export function isValidPhone(tel, country) {
  const digits = toDigits(tel);
  if (!digits) return false;

  // 1) 命中任一已支持国家/地区规则（中国 / 阿联酋 / 沙特 / 巴西）→ 通过
  if (COUNTRY_PHONE_RULES.some((r) => r.re.test(digits))) return true;

  // 2) 已明确选择了受支持的国家 → 号码必须匹配该国规则，否则拒绝
  if (findCountryRule(country)) return false;

  // 3) 其它国家 → 通用国际号码兜底（6~15 位）
  return /^\d{6,15}$/.test(digits);
}



// WEBPACK FOOTER //
// ./src/utils/validatePhone.js